/**
 * Eigenbau: Zeitband mit Bahnen und Monatsachse — für Meilensteine.
 *
 * Der Entwurf zeigt den Plan als ein Band: links die Bahnnamen (Mono, rechts
 * ausgerichtet), rechts eine Monatsachse, darunter je Bahn ein Balken, dessen
 * Füllung den Stand trägt. „Der ganze Plan in einem Blick, ohne Diagramm."
 *
 * `heute` ist ein Prozentwert der Bandbreite (0–100); die Heute-Linie steht
 * senkrecht durch alle Bahnen, mit dem Wort „heute" an der Achse.
 */

import type { ReactNode } from "react";
import { cx } from "@/lib/utils";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";

export type Zeitbahn = {
  name: string;
  /** Linke Kante in Prozent der Bandbreite. */
  links: string;
  /** Breite des Planbalkens in Prozent. */
  breite: string;
  /** Füllung des Planbalkens in Prozent seiner Breite. */
  fuellung: string;
  /** Grundfläche des Balkens — der Plan. */
  grund: string;
  /** Füllfarbe — der Stand. */
  fuell: string;
  /** Optionale Live-Füllung — eine Komponente, die echte Daten lädt. */
  fuellungInhalt?: ReactNode;
};

export function Zeitband({
  achse,
  bahnen,
  heute,
  className,
}: {
  /** Monatsnamen von links nach rechts. */
  achse: readonly string[];
  bahnen: readonly Zeitbahn[];
  /** Position der Heute-Linie in Prozent, z. B. "47.7%". */
  heute: string;
  className?: string;
}) {
  return (
    <div className={cx("flex", className)}>
      <div className="w-[132px] flex-none">
        <div className="h-[25px]" />
        {bahnen.map((bahn) => (
          <div
            key={bahn.name}
            className="flex h-[26px] items-center justify-end whitespace-nowrap pr-4 font-mono text-[10.5px] text-tinte3"
          >
            {bahn.name}
          </div>
        ))}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex font-mono text-[10.5px] text-tinte4">
          {/* Der Platz in der Achse ist die Kennung, nicht der Name: Sobald
              das Fenster über ein Jahr hinausreicht, kommt „Aug" ein zweites
              Mal — als React-Schlüssel wäre das eine Kollision. */}
          {achse.map((monat, stelle) => (
            <span
              key={stelle}
              className="flex-1 border-l border-linie pb-[9px] pl-[7px]"
            >
              {monat}
            </span>
          ))}
        </div>
        <div className="relative border-t border-tinte px-0 pb-[22px] pt-1">
          <span
            aria-hidden="true"
            className="absolute bottom-[18px] top-0 w-px bg-akzent"
            style={{ left: heute }}
          />
          {/* Das Label folgt der Linie — ohne `left` bliebe es am Bandanfang
              stehen, sobald die Linie mitten im Band steht. */}
          <span
            className="absolute bottom-0 -translate-x-1/2"
            style={{ left: heute }}
          >
            <Mikrolabel grad={10} ton="akzent">
              heute
            </Mikrolabel>
          </span>
          {bahnen.map((bahn) => (
            <div key={bahn.name} className="relative h-[26px]">
              <span
                className="absolute top-[6px] h-[14px] shadow-[inset_0_0_0_1px_var(--linie3)]"
                style={{
                  left: bahn.links,
                  width: bahn.breite,
                  background: bahn.grund,
                }}
              >
                {bahn.fuellungInhalt ?? (
                  <span
                    className="block h-full"
                    style={{ width: bahn.fuellung, background: bahn.fuell }}
                  />
                )}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
